import {useEffect, useRef, useState} from "react";
import {Spinner} from "@chakra-ui/react";

/**
 * このコンポーネントは、iframeを使って外部のカレンダーを表示し、
 * ユーザーがiframe内でクリックしたり、一定時間が経過したりした場合に
 * iframeをリロードする仕組みを持っています。
 * また、iframeが読み込まれている間はloadingのスピナーを表示し、
 * 背景は半透明に設定して、読み込み中であることを視覚的に伝え、何度もクリックされるのを防ぎます。
 *
 * 主な機能:
 * 1. iframeからのメッセージを受信して、iframeの高さや状態を更新。
 * 2. iframe内のクリックを監視して、タイマーをリセット。
 * 3. iframe外のクリック、スクロール、マウス移動、タッチなどのユーザーの動作を監視して、タイマーをリセット。
 * 3. 10分間の非アクティブ状態後にiframeをリロードする。
 * 4. iframeが読み込まれている間にスピナー（回転中のアイコン）を表示。
 */

const EventCalender = () => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [iframeHeight, setIframeHeight] = useState("320px");
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [isIframeClicked, setIsIframeClicked] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // iframe からのメッセージを受信する処理
    useEffect(() => {
        const receiveMessage = (event: any) => {
            if (event.data.height) {
                setIframeHeight(`${event.data.height}px`);
            }
            if (event.data.type === "clicked") {
                setIsIframeClicked(true);
            }
            if (event.data.type === "loading") {
                setIsLoading(event.data.loading);
            }

        };
        window.addEventListener("message", receiveMessage);

        //初回ローディングを行う
        setIsLoading(true);

        return () => {
            window.removeEventListener("message", receiveMessage);
        };
    }, []);

    // iframe をリロードする関数
    const reloadIframe = () => {
        if (iframeRef.current) {
            iframeRef.current.src = "https://ucrca.sakura.ne.jp/dev/proxy.php?url=https://www.konan-u.ac.jp/calendar/";
        }
    };

    // タイマーを設定して、一定時間後にデフォルト画面に遷移
    const setTimer = () => {
        // もし既存のタイマーがあれば、クリアして新しいタイマーを設定
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            reloadIframe();
        }, 600000); // 10分後にリロード
    };

    // handleLoad 内で setTimer を実行する
    const handleLoad = () => {
        console.log("iframeRef.current", iframeRef.current);
        setIsLoading(true);
        if (!iframeRef.current) return;
        try {
            // `iframe` に "remove-element" というメッセージを送る
            iframeRef.current.contentWindow?.postMessage("remove-element", "*");
            // iframeが読み込まれた後にタイマーを設定
            setTimer();

        } catch (error) {
            console.error("iframe へのメッセージ送信に失敗しました:", error);
        }
    };

    // ユーザーの動作（クリック、スクロール）でタイマーをリセットする処理
    const resetTimer = () => {
        setTimer(); // ユーザーの動作があったらタイマーをリセット
    };

    // これはIframe内以外の場所をクリックした時に呼び出されるためのもの。
    useEffect(() => {
        // ユーザーの動作を監視して、タイマーをリセット
        const events = ['click', 'scroll', 'mousemove', "touchstart"];
        events.forEach(event => {
            window.addEventListener(event, resetTimer);
        });
        return () => {
            // クリーンアップでイベントリスナーを解除
            events.forEach(event => {
                window.removeEventListener(event, resetTimer);
            });
        };
    }, []);

    // これはIframe内でクリックした時に呼び出されるためのもの。
    useEffect(() => {
        if (isIframeClicked) {
            setTimer();
            setIsIframeClicked(false);
        }
    }, [isIframeClicked]);

    return (
        <>
            <div style={{
                position: "absolute",
                width: window.innerHeight > window.innerWidth ? "100%" : "80%",
                height: "100vh",
                overflowY: "scroll",
                paddingTop: "5%",
                touchAction: "pan-y",
            }}
            >
                <iframe
                    ref={iframeRef}
                    onLoad={handleLoad}
                    src="https://ucrca.sakura.ne.jp/dev/proxy.php?url=https://www.konan-u.ac.jp/calendar/"
                    width="100%"
                    height={iframeHeight}
                    style={{border: "none", transition: "height 0.3s ease-in-out"}}
                    sandbox="allow-scripts allow-same-origin" // 新しいウィンドウを開かないように制限
                />
            </div>
            {isLoading && (
                <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: window.innerHeight > window.innerWidth ? "100%" : "80%",
                    height: "100%",
                    backgroundColor: "rgba(0, 0, 0, 0.4)", // 半透明の背景
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "white",
                    zIndex: 10,
                }}>
                    <Spinner/>
                </div>
            )}
        </>

    );
}
export default EventCalender;
